import type {
  SampleDataFieldError,
  SampleDataTemplateContent,
  SamplePlayer,
} from '../../../types/sampleData';
import { SCHOLARSHIP_PLAYER_POSITIONS } from '../../../types/scholarships';

export const replaceAt = <T>(items: T[], index: number, patch: Partial<T>): T[] =>
  items.map((item, i) => (i === index ? { ...item, ...patch } : item));

export const removeAt = <T>(items: T[], index: number): T[] => items.filter((_, i) => i !== index);

/**
 * Removes a player and clears every reference to it, so no call, note, placement or duplicate pair
 * is left pointing at a key that no longer exists. The cleared pickers show empty, which is the
 * prompt to choose someone else.
 */
export const removePlayer = (
  content: SampleDataTemplateContent,
  playerKey: string
): SampleDataTemplateContent => {
  const clear = (key: string) => (key === playerKey ? '' : key);
  const pair = content.duplicatePairPlayerKeys;
  const nextPair = pair ? ([clear(pair[0]), clear(pair[1])] as [string, string]) : null;
  return {
    ...content,
    players: content.players.filter((p) => p.key !== playerKey),
    calls: content.calls.map((c) => ({ ...c, playerKey: clear(c.playerKey) })),
    notes: content.notes.map((n) => ({ ...n, playerKey: clear(n.playerKey) })),
    rosterBoard: content.rosterBoard && {
      ...content.rosterBoard,
      placements: content.rosterBoard.placements.map((p) => ({
        ...p,
        playerKey: clear(p.playerKey),
      })),
    },
    duplicatePairPlayerKeys: nextPair && (nextPair[0] || nextPair[1]) ? nextPair : null,
  };
};

/** Removes a team and unassigns every player who was on it. */
export const removeTeam = (
  content: SampleDataTemplateContent,
  teamKey: string
): SampleDataTemplateContent => ({
  ...content,
  teams: content.teams.filter((t) => t.key !== teamKey),
  players: content.players.map((p) => (p.teamKey === teamKey ? { ...p, teamKey: null } : p)),
});

/** A DOM id for the input bound to an API field path, e.g. `players[0].phone`. */
export const fieldId = (path: string): string =>
  `sample-data-${path.replace(/[^a-zA-Z0-9]+/g, '-').replace(/-+$/, '')}`;

/** Lower-cased, so `Players[0].Phone` from a PascalCase serializer still lands on its input. */
export const errorKey = (path: string): string => path.toLowerCase();

export type FieldErrorLookup = (path: string) => string | undefined;

export const buildFieldErrorLookup = (errors: SampleDataFieldError[]): FieldErrorLookup => {
  const byField = new Map<string, string>();
  for (const error of errors) {
    const key = errorKey(error.field);
    // Several rules can fail on one field; show them together rather than dropping all but one.
    byField.set(key, byField.has(key) ? `${byField.get(key)} ${error.message}` : error.message);
  }
  return (path) => byField.get(errorKey(path));
};

const COLLECTION_LABELS: Record<string, string> = {
  teams: 'Team',
  players: 'Player',
  calls: 'Call',
  notes: 'Note',
  events: 'Event',
  games: 'game',
  placements: 'placement',
};

const SEGMENT_LABELS: Record<string, string> = {
  timezone: 'Time zone',
  rosterboard: 'Roster board',
  duplicatepairplayerkeys: 'Duplicate pair',
  playerkey: 'player',
  teamkey: 'team',
  dayoffset: 'days from seeding',
  startdayoffset: 'start day',
  enddayoffset: 'end day',
  gradyear: 'grad year',
  sortorder: 'sort order',
};

const humanize = (segment: string): string =>
  SEGMENT_LABELS[segment.toLowerCase()] ?? segment.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase();

/** Turns `events[1].games[0].time` into "Event 2, game 1, time" for the error summary. */
export const describeField = (path: string): string => {
  if (!path) return 'Template';
  const parts = path.split('.').map((segment) => {
    const match = /^(\w+)\[(\d+)\]$/.exec(segment);
    if (!match) return humanize(segment);
    const label = COLLECTION_LABELS[match[1].charAt(0).toLowerCase() + match[1].slice(1)];
    return `${label ?? humanize(match[1])} ${Number(match[2]) + 1}`;
  });
  const text = parts.join(', ');
  return text.charAt(0).toUpperCase() + text.slice(1);
};

export const playerName = (player: SamplePlayer, index: number): string =>
  `${player.firstName} ${player.lastName}`.trim() || `Player ${index + 1}`;

export interface KeyedOption {
  key: string;
  label: string;
}

/**
 * Player picker options. `SelectPopover` is keyed by label, and the duplicate pair exists precisely
 * to hold two players with the same name, so every label carries the row number to stay unique.
 */
export const playerOptions = (players: SamplePlayer[]): KeyedOption[] =>
  players.map((player, index) => ({
    key: player.key,
    label: `${index + 1}. ${playerName(player, index)}${player.gradYear ? ` (${player.gradYear})` : ''}`,
  }));

/**
 * The API checks positions against the closed roster set (`ScholarshipPlayerPositions`), so the
 * picker offers exactly those codes. Free text here would only fail on save.
 */
export const POSITION_CHOICES: KeyedOption[] = SCHOLARSHIP_PLAYER_POSITIONS.map((position) => ({
  key: position,
  label: position,
}));

/** What every section of the template form receives. */
export interface SampleSectionProps {
  value: SampleDataTemplateContent;
  onChange: (next: SampleDataTemplateContent) => void;
  errorFor: FieldErrorLookup;
}
